import { registerSW } from 'virtual:pwa-register';

/**
 * Service worker registration, with the reload deliberately gated.
 *
 * The plugin is configured as `registerType: 'prompt'`, which leaves a new
 * worker waiting instead of taking over immediately. That is not because we
 * want to ask the user — a shop device has nobody who would tap "update", and
 * left to them the app runs last month's JavaScript forever. It is because
 * `autoUpdate` reloads the instant the new worker activates, and that can land
 * in the middle of a generation: the fetch dies, the customer sees
 * an error, and Gemini has already been paid for an image nobody receives.
 *
 * So: adopt every update automatically, but only while nothing is generating.
 *
 * On iOS this matters twice over — a standalone PWA reload is a cold start, not
 * a resumed session, so an ill-timed update also drops the in-memory result rail
 * (which is why results are mirrored into IndexedDB).
 */

let applyUpdate: ((reloadPage?: boolean) => Promise<void>) | null = null;
let updateIsWaiting = false;
let criticalWorkCount = 0;

function applyIfIdle(): void {
  if (updateIsWaiting && criticalWorkCount === 0 && applyUpdate) {
    updateIsWaiting = false;
    void applyUpdate(true);
  }
}

export function initServiceWorker(): void {
  applyUpdate = registerSW({
    immediate: true,
    onNeedRefresh() {
      updateIsWaiting = true;
      applyIfIdle();
    },
  });
}

/**
 * Marks the start of work that must not be interrupted by an update — i.e. an
 * in-flight generation. Balanced by `endCriticalWork`.
 */
export function beginCriticalWork(): void {
  criticalWorkCount += 1;
}

export function endCriticalWork(): void {
  criticalWorkCount = Math.max(0, criticalWorkCount - 1);
  applyIfIdle();
}

/** Exposed for tests and diagnostics. */
export function hasPendingUpdate(): boolean {
  return updateIsWaiting;
}
